import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, ShieldCheck, Loader2, TrendingUp, Sparkles, Check,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
} from "lucide-react";
import client from "../lib/api";
import "../components/pulse.css";

/**
 * The dedicated Weekly Pulse screen. Two fast one-to-five questions
 * (personalized and rotating), a motivation line that adapts to the band
 * of the latest pulse, and a motivation-trend chart with the weekly logs.
 * Reads and writes /engagement/weekly-checkin. Owner only, never scored.
 */

const SCALE = {
  1: { light: "#FF8A8A", deep: "#E8492E" },
  2: { light: "#FFC48A", deep: "#F0872E" },
  3: { light: "#FFE08A", deep: "#E8B21E" },
  4: { light: "#B6E88F", deep: "#5FB733" },
  5: { light: "#8FE8B4", deep: "#0E9E7E" },
};

const BANDS = {
  1: { q: "Foggy is a starting line, not a verdict. You showed up, and that is the whole game this week.", tag: "one step at a time", grad: "linear-gradient(135deg,#C43B23,#8F2A18)", sh: "rgba(143,42,24,.35)" },
  2: { q: "You are still early. Every messy week is data your future self will thank you for.", tag: "keep gathering", grad: "linear-gradient(135deg,#C46A16,#8F4A0C)", sh: "rgba(143,74,12,.35)" },
  3: { q: "The middle is where most people quit and most breakthroughs hide. You are right on the edge.", tag: "stay with it", grad: "linear-gradient(135deg,#B07E0E,#7A5608)", sh: "rgba(122,86,8,.35)" },
  4: { q: "Things are clicking. Protect what is working and let it compound, week over week.", tag: "momentum is real", grad: "linear-gradient(135deg,#3F8F27,#276018)", sh: "rgba(39,96,24,.35)" },
  5: { q: "This is what clarity feels like. Remember this week, and build the next one from it.", tag: "you found your footing", grad: "linear-gradient(135deg,#0B7A62,#0A4F63)", sh: "rgba(10,79,99,.35)" },
};

const DEFAULT_Q = [
  { label: "How clear does your direction feel this week?", low: "still foggy", high: "crystal clear" },
  { label: "Your open question: which way is it leaning this week?", low: "leaning no", high: "leaning yes" },
];

const clamp = (v) => Math.max(1, Math.min(5, v));
const mean = (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0);
const PER = 5;

function Pill({ v, on, disabled, onPick }) {
  const s = SCALE[v];
  return (
    <button
      type="button"
      className={`pw-pill ${on ? "on" : ""}`}
      style={{ "--pw-pdeep": s.deep, "--pw-plight": s.light, "--pw-pshadow": `${s.deep}55` }}
      aria-pressed={on}
      aria-label={`rate ${v}`}
      disabled={disabled}
      onClick={() => onPick(v)}
    >
      {v}
    </button>
  );
}

export default function WeeklyPulseScreen({ product }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [logged, setLogged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);

  const load = useCallback((keepAnswers) => {
    client.get(`/engagement/weekly-checkin?product=${product}`)
      .then((r) => {
        const data = r.data || null;
        setState(data);
        if (data && !data.due) {
          const cur = (data.history || []).find((h) => h.week === data.week);
          if (cur && !keepAnswers) { setQ1(cur.q1); setQ2(cur.q2); }
          setLogged(true);
        }
      })
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  }, [product]);
  useEffect(() => { load(false); }, [load]);

  const questions = state?.questions && state.questions.length >= 2 ? state.questions : DEFAULT_Q;

  // Every logged week as a chart/log row, oldest to newest.
  const weeks = useMemo(() => {
    const hist = state?.history || [];
    return hist.map((h, i) => ({
      wk: `W${i + 1}`,
      date: h.date || "",
      dir: h.q1 || 3,
      lean: h.q2 || 3,
      note: h.note || "",
      week: h.week,
      live: !!(state && h.week === state.week && logged),
    }));
  }, [state, logged]);

  const submit = async () => {
    if (!(q1 && q2) || busy || logged) return;
    setBusy(true);
    try {
      await client.post("/engagement/weekly-checkin", { product, q1, q2 });
      setLogged(true);
      setPage(0);
      load(true);
    } catch (_e) { /* silent */ }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="pw-screen">
        <div className="pw-col" style={{ alignItems: "center", paddingTop: 120 }} data-testid="pulse-loading">
          <Loader2 size={26} className="animate-spin" style={{ color: "#8C90AC" }} />
        </div>
      </div>
    );
  }

  const latest = weeks.length ? weeks[weeks.length - 1] : null;
  const band = latest ? clamp(Math.round((latest.dir + latest.lean) / 2)) : 3;
  const B = BANDS[band];

  const ready = q1 != null && q2 != null;
  const btnClass = logged ? "pw-logbtn done" : ready ? "pw-logbtn ready" : "pw-logbtn";
  const btnLabel = logged ? "Week logged" : "Log this week";

  // stats
  const dirs = weeks.map((w) => w.dir);
  const leans = weeks.map((w) => w.lean);
  const avgDir = mean(dirs);
  const avgLean = mean(leans);
  const momentum = weeks.length ? weeks[weeks.length - 1].dir - weeks[0].dir : 0;
  const momUp = momentum >= 0;

  // chart geometry
  const padL = 34, padR = 18, padT = 18, padB = 30, W = 680, H = 200;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = weeks.length;
  const xFor = (i) => (n <= 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW);
  const yFor = (v) => padT + ((5 - v) / 4) * plotH;
  const dirPts = weeks.map((w, i) => `${xFor(i).toFixed(1)},${yFor(w.dir).toFixed(1)}`).join(" ");
  const leanPts = weeks.map((w, i) => `${xFor(i).toFixed(1)},${yFor(w.lean).toFixed(1)}`).join(" ");
  let areaD = "";
  if (n) {
    areaD = "M" + weeks.map((w, i) => `${i ? " L" : ""}${xFor(i).toFixed(1)} ${yFor(w.dir).toFixed(1)}`).join("");
    areaD += ` L${xFor(n - 1).toFixed(1)} ${yFor(1).toFixed(1)} L${xFor(0).toFixed(1)} ${yFor(1).toFixed(1)} Z`;
  }

  // logs, newest first, paginated
  const rows = weeks.slice().reverse();
  const pages = Math.max(1, Math.ceil(rows.length / PER));
  const safePage = Math.min(page, pages - 1);
  const pageRows = rows.slice(safePage * PER, safePage * PER + PER);

  return (
    <div className="pw-screen" data-testid="weekly-pulse-screen">
      <div className="pw-col">
        {/* HEADER */}
        <header className="pw-rise pw-d0">
          <span className="pw-eyebrow"><Activity size={15} /> Weekly Pulse</span>
          <h1 className="pw-h1">A two-tap read on <span className="pw-mark">your week.</span></h1>
          <p className="pw-sub">Once a week, tell Klair how the week actually felt. The two questions change with your weeks, chosen for who you are and what you are chasing. Quick on purpose.</p>
        </header>

        {/* PULSE CARD */}
        <section className="pw-glass pw-rise pw-d1" aria-label="Update this week">
          <div className="pw-accent"></div>
          <div className="pw-kicker">{logged ? "Logged this week" : "Update this week"}</div>

          {[0, 1].map((qi) => {
            const q = questions[qi] || DEFAULT_Q[qi];
            const val = qi === 0 ? q1 : q2;
            const setVal = qi === 0 ? setQ1 : setQ2;
            return (
              <div className="pw-q" key={qi} data-testid={`pulse-q${qi + 1}`}>
                <div className="pw-qtext">{q.label}</div>
                <div className="pw-pills">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <Pill key={v} v={v} on={val === v} disabled={logged} onPick={setVal} />
                  ))}
                </div>
                <div className="pw-caps"><span>{q.low}</span><span>{q.high}</span></div>
              </div>
            );
          })}

          <button className={btnClass} type="button" disabled={!ready || busy || logged} onClick={submit} data-testid="pulse-log">
            {logged ? <Check size={18} /> : <Sparkles size={18} />}
            <span>{busy ? "Saving" : btnLabel}</span>
          </button>
        </section>

        {/* MOTIVATION QUOTE */}
        <section className="pw-quote pw-rise pw-d2" style={{ background: B.grad, boxShadow: `0 20px 50px ${B.sh}` }} aria-label="A line for your week" data-testid="pulse-quote">
          <div className="pw-quote-mark" aria-hidden="true">&ldquo;</div>
          <div className="pw-quote-text">{B.q}</div>
          <div className="pw-quote-tag"><span className="pw-quote-rule"></span><span className="pw-quote-tagtext">{B.tag}</span></div>
        </section>

        {/* TREND CARD */}
        <section className="pw-glass pw-glass-60 pw-rise pw-d3" aria-label="Your motivation trend">
          <div className="pw-trend-head">
            <span className="pw-trend-title"><TrendingUp size={17} /> Your motivation trend</span>
            <span className="pw-badge" data-testid="pulse-weeks-badge">{n} {n === 1 ? "week" : "weeks"} logged</span>
          </div>
          <p className="pw-howto">How to read it: each point is one weekly pulse. Higher is clearer and more positive. Watch the shape, not any single week.</p>

          {n === 0 ? (
            <p className="pw-howto" style={{ marginTop: 18 }} data-testid="pulse-trend-empty">Log your first week above and your trend starts drawing itself here.</p>
          ) : (
            <>
              <div className="pw-stats">
                <div className="pw-stat">
                  <div className="pw-stat-label">Avg direction</div>
                  <div className="pw-stat-val" style={{ color: "#0E9E7E" }}>{avgDir.toFixed(1)}</div>
                  <div className="pw-stat-trend" style={{ color: "#0E9E7E" }}><ArrowUp size={13} /> clarity building</div>
                </div>
                <div className="pw-stat">
                  <div className="pw-stat-label">Avg leaning</div>
                  <div className="pw-stat-val" style={{ color: "#1D5FD4" }}>{avgLean.toFixed(1)}</div>
                  <div className="pw-stat-trend" style={{ color: "#1D5FD4" }}><ArrowUp size={13} /> trending positive</div>
                </div>
                <div className="pw-stat">
                  <div className="pw-stat-label">Momentum</div>
                  <div className="pw-stat-val" style={{ color: momUp ? "#0E9E7E" : "#E8492E" }}>{momentum > 0 ? "+" : ""}{momentum}</div>
                  <div className="pw-stat-trend" style={{ color: momUp ? "#0E9E7E" : "#E8492E" }}>
                    {momUp ? <ArrowUp size={13} /> : <ArrowDown size={13} />} {momUp ? "up since week 1" : "dipped since w1"}
                  </div>
                </div>
              </div>

              <div className="pw-chart-panel">
                <div className="pw-legend">
                  <span><span className="pw-swatch" style={{ background: "#0B6A57" }}></span> Direction clarity</span>
                  <span><span className="pw-swatch" style={{ background: "#1D5FD4" }}></span> Which way it leans</span>
                </div>
                <div className="pw-chart">
                  <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Weekly direction and leaning trend">
                    <defs>
                      <linearGradient id="pwArea" gradientUnits="userSpaceOnUse" x1="0" y1={padT} x2="0" y2={yFor(1)}>
                        <stop offset="0" stopColor="#0E9E7E" stopOpacity="0.18" />
                        <stop offset="1" stopColor="#0E9E7E" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {[1, 2, 3, 4, 5].map((v) => (
                      <g key={v}>
                        <line x1={padL} y1={yFor(v)} x2={W - padR} y2={yFor(v)} stroke="#EDEBF6" strokeWidth="1" />
                        {(v === 1 || v === 3 || v === 5) && (
                          <text x={padL - 9} y={yFor(v) + 4} textAnchor="end" fontFamily="Archivo, sans-serif" fontSize="11" fontWeight="700" fill="#B4B8D4">{v}</text>
                        )}
                      </g>
                    ))}
                    {areaD && <path d={areaD} fill="url(#pwArea)" />}
                    <polyline points={dirPts} fill="none" stroke="#0E9E7E" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                    <polyline points={leanPts} fill="none" stroke="#1D5FD4" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="1 7" />
                    {weeks.map((w, i) => (
                      <g key={i}>
                        <circle cx={xFor(i)} cy={yFor(w.dir)} r={w.live ? 6 : 4} fill="#fff" stroke="#0E9E7E" strokeWidth={w.live ? 3 : 2} />
                        <circle cx={xFor(i)} cy={yFor(w.lean)} r={w.live ? 6 : 4} fill="#fff" stroke="#1D5FD4" strokeWidth={w.live ? 3 : 2} />
                        <text x={xFor(i)} y={H - 10} textAnchor="middle" fontFamily="Archivo, sans-serif" fontSize="11" fontWeight={w.live ? 800 : 700} fill={w.live ? "#0E9E7E" : "#A3A7C0"}>{w.wk}</text>
                      </g>
                    ))}
                  </svg>
                </div>
              </div>

              <div className="pw-logs-head">
                <span className="pw-logs-label">Your weekly logs</span>
                <div className="pw-pager">
                  <span className="pw-pager-info">Page {safePage + 1} of {pages}</span>
                  <button className="pw-pager-btn" type="button" aria-label="newer" disabled={safePage <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}><ChevronLeft size={14} /></button>
                  <button className="pw-pager-btn" type="button" aria-label="older" disabled={safePage >= pages - 1} onClick={() => setPage((p) => p + 1)}><ChevronRight size={14} /></button>
                </div>
              </div>
              <div data-testid="pulse-logs">
                {pageRows.map((w) => (
                  <div key={w.week} className={`pw-log-row ${w.live ? "live" : ""}`}>
                    <div><div className="pw-log-wk">{w.wk}</div><div className="pw-log-date">{w.date}</div></div>
                    <div className="pw-log-note">{w.note}</div>
                    <div className="pw-log-chips">
                      <span className="pw-chip" style={{ background: SCALE[clamp(w.dir)].deep }} title={`direction ${w.dir}`}>{w.dir}</span>
                      <span className="pw-chip" style={{ background: SCALE[clamp(w.lean)].deep }} title={`leaning ${w.lean}`}>{w.lean}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        {/* PRIVACY */}
        <div className="pw-privacy pw-rise pw-d3">
          <ShieldCheck size={20} style={{ color: "#0E9E7E" }} />
          <span>Private to you. This is your own read of your week, never a score and never a ranking.</span>
        </div>
      </div>
    </div>
  );
}
